import {
  Box,
  Button,
  Center,
  Flex,
  Input,
  Show,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import { INSTITUTION_SUBMIT } from "@constants/analytics";
import React, { SyntheticEvent, useState } from "react";
import { trackEvent, validateEmail } from "utils";

const options = [
  { label: "<1k ETH", value: "<1" },
  { label: "1k - 5k ETH", value: "1-5" },
  { label: ">5k ETH", value: ">5" },
];

const initialData = {
  name: "",
  email: "",
  company: "",
  expectedInvestment: options[0].value,
};

const ContactUs = () => {
  const toast = useToast();

  const [formData, setFormData] = useState(initialData); // Adjust based on your needs
  const [isSubmitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({ name: "", email: "", company: "" });

  const handleInputChange = (key: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateForm = () => {
    let errors = {} as any;
    if (!formData.name.trim()) errors.name = "Name is required.";
    if (!formData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!validateEmail(formData.email)) {
      errors.email = "Invalid email.";
    }
    if (!formData.company.trim()) errors.company = "Company name is required";

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    trackEvent(INSTITUTION_SUBMIT, formData);
    setSubmitting(true);

    try {
      await fetch(
        "https://script.google.com/macros/s/AKfycbzN-afSWdgwHcrSZEHN6B18hsjNQAMxbn6qJXmM3dclrTjsiTszmyXbUTw_oFnTm-eS/exec",
        {
          method: "POST",
          mode: "no-cors",
          redirect: "follow",
          headers: {
            "Content-Type": "text/plain;charset=utf-8",
          },
          body: JSON.stringify(Object.values(formData)),
        }
      );

      toast({
        description: "Data submitted successfully!",
        status: "success",
        duration: 2000,
        position: "top",
        isClosable: false,
      });
      setFormData(initialData);
    } catch (error) {
      console.error("Error:", error);

      toast({
        description: "An error occurred. Please try again.",
        status: "error",
        duration: 2000,
        position: "top",
        isClosable: false,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex
      id="contact-us"
      maxWidth="1200px"
      w="100%"
      h={{ base: 940, md: 1060 }}
      py={{ base: "70px", md: 20 }}
      mb={{ md: 20 }}
      px={3}
      pos="relative"
      textAlign="center"
      alignItems="center"
      flexDir="column"
      borderRadius={{ base: 0, md: "43px" }}
      overflow={"hidden"}
      bg={{ base: "#075A5A", md: "transparent" }}
    >
      <Show above="md">
        <BaseImage
          src="/assets/institutions/contact/bg.svg"
          alt="hero"
          objectFit="cover"
          fill
        />
      </Show>
      <Center
        zIndex={2}
        flexDir="column"
        gap={"72px"}
        maxW={{ base: 336, lg: 1071 }}
      >
        <Box>
          <Text
            color="#fff"
            fontSize={40}
            lineHeight={"48px"}
            pos="relative"
            fontWeight={500}
          >
            Ready to get started?
          </Text>
          <Text
            mt={3}
            color="#fff"
            fontSize={20}
            lineHeight={"24px"}
            pos="relative"
            fontWeight={500}
          >
            Fill out this form and we&apos;ll be in touch to guide you.
          </Text>
        </Box>
      </Center>
      <Box
        w={{ base: "100%", md: 600 }}
        mt={"72px"}
        zIndex={10}
        p={{ base: 3, md: "60px" }}
        borderRadius={16}
        bg="#BDDBDB"
        boxShadow={"0px 32px 71px rgba(0, 0, 0, 0.10)"}
        textAlign={"left"}
      >
        <form onSubmit={handleSubmit}>
          <Text fontSize={18} lineHeight={"21.60px"} fontWeight={700}>
            NAME
          </Text>
          <Input
            key="name"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            mt={3}
            bg="#D8F0F0"
            fontSize={24}
            fontWeight={600}
            lineHeight={"28px"}
            p={6}
          />
          {errors.name && (
            <Text textStyle="p14" color="#e53e3e" mt="0.5rem" textAlign="left">
              {errors.name}
            </Text>
          )}
          <Text fontSize={18} lineHeight={"21.60px"} fontWeight={700} mt={8}>
            EMAIL
          </Text>
          <Input
            key="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            mt={3}
            bg="#D8F0F0"
            fontSize={24}
            fontWeight={600}
            lineHeight={"28px"}
            p={6}
          />
          {errors.email && (
            <Text textStyle="p14" color="#e53e3e" mt="0.5rem" textAlign="left">
              {errors.email}
            </Text>
          )}
          <Text fontSize={18} lineHeight={"21.60px"} fontWeight={700} mt={8}>
            COMPANY
          </Text>
          <Input
            key="company"
            value={formData.company}
            onChange={(e) => handleInputChange("company", e.target.value)}
            mt={3}
            bg="#D8F0F0"
            fontSize={24}
            fontWeight={600}
            lineHeight={"28px"}
            p={6}
          />
          {errors.company && (
            <Text textStyle="p14" color="#e53e3e" mt="0.5rem" textAlign="left">
              {errors.company}
            </Text>
          )}
          <Text fontSize={18} lineHeight={"21.60px"} fontWeight={700} mt={8}>
            EXPECTED INVESTMENT
          </Text>
          <Flex gap={3} mt={3} flexWrap={"wrap"}>
            {options.map((option) => (
              <Box
                key={option.value}
                role="button"
                fontSize={24}
                lineHeight={"28px"}
                fontWeight={600}
                borderRadius={16}
                p={"12px 20px"}
                whiteSpace="nowrap"
                onClick={() =>
                  handleInputChange("expectedInvestment", option.value)
                }
                color={
                  option.value === formData.expectedInvestment
                    ? "#FFFFFF"
                    : "inherit"
                }
                bg={
                  option.value === formData.expectedInvestment
                    ? "#2F9999"
                    : "#D8F0F0"
                }
              >
                {option.label}
              </Box>
            ))}
          </Flex>
          <Button
            mt={12}
            bg="#075A5A"
            w="100%"
            p={6}
            type="submit"
            disabled={isSubmitting}
            _hover={{
              bg: "#075A5A",
            }}
          >
            {isSubmitting ? (
              <Spinner size="md" color="primary" />
            ) : (
              <Text
                color="white"
                fontSize={{ base: 18, md: 20 }}
                lineHeight={"24px"}
                fontWeight={600}
              >
                SUBMIT
              </Text>
            )}
          </Button>
        </form>
      </Box>
    </Flex>
  );
};

export default ContactUs;
